import { PERMISSION_ENTITY_INVOICE } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'

import type { InvoiceCreateWire } from '@klear/invoice/invoice.wire'

export type InvoiceEventSnapshot = Omit<InvoiceCreateWire, 'orderId'> & { type: string; id?: number }

export type InvoiceJson = {
  orderId: number
  id?: number
  parentId?: number
  number?: string
  amount: Money
  effectiveDate: KlearDate
  endDate: KlearDate
  notes?: string
  idDisplay?: string
}

export class Invoice {
  static readonly __klearClass = 'Invoice'

  readonly orderId: number
  id?: number
  parentId?: number
  number?: string
  amount: Money
  effectiveDate: KlearDate
  endDate: KlearDate
  notes?: string
  idDisplay: string

  constructor(props: InvoiceJson) {
    this.orderId = props.orderId
    this.id = props.id
    this.parentId = props.parentId
    this.number = props.number
    this.amount = props.amount
    this.effectiveDate = props.effectiveDate
    this.endDate = props.endDate
    this.notes = props.notes
    this.idDisplay = props.idDisplay ?? ''
  }

  get isPersisted(): boolean {
    return isNumber(this.id) && this.id > 0
  }

  assertValid(): void {
    if (!(this.orderId > 0)) {
      throw new Error('Invoice requires an order id')
    }
    if (this.amount.lessThan(this.amount.zero)) {
      throw new Error('Invoice amount must be zero or greater')
    }
  }

  toJSON(): InvoiceJson {
    return {
      orderId: this.orderId,
      id: this.id,
      parentId: this.parentId,
      number: this.number,
      amount: this.amount,
      effectiveDate: this.effectiveDate,
      endDate: this.endDate,
      notes: this.notes,
      idDisplay: this.idDisplay
    }
  }
}

function parseDate(value: unknown, fallback: KlearDate): KlearDate {
  if (value instanceof KlearDate) return value
  if (isString(value) && value.length > 0) {
    const parsed = KlearDate.parse(value)
    if (parsed) return parsed
  }
  return fallback
}

export function invoiceFromEventData(
  orderId: number,
  data: Omit<InvoiceCreateWire, 'orderId'>,
  ctx?: { id?: number; parentEventId?: number }
): Invoice {
  const effectiveDate = parseDate(data.effectiveDate, KlearDate.today())
  const endDate = parseDate(data.endDate, effectiveDate)

  return new Invoice({
    orderId,
    id: ctx?.id,
    parentId: ctx?.parentEventId ?? data.parentEventId,
    number: data.number ?? undefined,
    amount: new Money(data.amount),
    effectiveDate,
    endDate,
    notes: data.notes ?? undefined
  })
}

export function invoiceToEventData(invoice: Invoice): InvoiceEventSnapshot {
  const data: InvoiceEventSnapshot = {
    type: PERMISSION_ENTITY_INVOICE,
    amount: invoice.amount.toJSON(),
    effectiveDate: invoice.effectiveDate.toJSON(),
    endDate: invoice.endDate.toJSON()
  }

  if (invoice.id != null && invoice.id > 0) data.id = invoice.id
  if (invoice.number != null) data.number = invoice.number
  if (invoice.notes != null) data.notes = invoice.notes

  return data
}
